import React, {Component} from 'react';
import {connect} from 'react-redux';
import debounce from "lodash.debounce";
import T from 'i18n-react';
// Style
import './InvoicePage.css';
//Components
import TableFilters from "../../components/tableComponent/filters/TableFilters";
import DynamicTable from "../../components/tableComponent/dynamicTable/DynamicTable";
import PdfViewerDialog from "../../components/pdfViewerDialog/PdfViewerDialog";
//Services
import FilterService from "../../services/FilterService";
import InvoiceService from "../../services/InvoiceService";
import {getDistinctValues} from "../../utils/JSUtils";
import {makeCancelable} from "../../utils/PromiseUtils";
import {downloadCsv} from "../../utils/ExportCsvUtils";
import RaisedButton from "material-ui/RaisedButton";
import INVOICE_CHECK_STATUS from "../../enums/InvoiceCheckStatus";

const HEADER_LIST = ["codeClient", "codeBu", "invoiceNumber", "period", "amount"];

const mapStateToProps = (state) => ({
    idUser: state.userState.userData.idUser,
    userProfile: state.userState.userData.profile,
    language: state.languageState.language,
    clientList: state.userState.userData.clientList,
    buList: state.userState.userData.businessUnitList
});

class InvoicePage extends Component {
    constructor(props) {
        super(props);

        const locationList = getDistinctValues(this.props.clientList.map(client => client.location));
        const hasOneLocation = locationList.length === 1;
        const location = hasOneLocation ? locationList[0] : null;

        this.state = {
            hasOneLocation,
            invoiceList: [],
            selectedInvoice: null,
            invoiceFileErrorMessage: null,
            invoiceFile: null,
            totalRowNumber: 0,
            numberOfRowsPerPage: 10,
            page: 1,
            error: null,
            isLoaded: false,
            showPdfViewer: false,
            filterList: {
                location,
                client: null,
                bu: null,
                year: null,
                monthList: [],
                searchText: null,
            }
        };

        this._promises = [];
        this.handleFilterChange = this.handleFilterChange.bind(this);
        this.resetFilters = this.resetFilters.bind(this);
        //Méthode avec debounce pour optimiser les appels (sur la recherche fulltext). cf. https://fr.reactjs.org/docs/faq-functions.html#debounce
        this.getInvoiceListDebounced = debounce(this.getInvoiceList, 300);
    }

    componentDidMount() {
        this.getInvoiceList();
    }

    /**
     * Annulation de toutes les promesses encore en cours lorsque
     * l'on quitte le composant
     */
    componentWillUnmount() {
        this._promises.forEach(promise => promise.cancel());
        this.getInvoiceListDebounced.cancel();
    }

    render() {
        return (
            <div className="invoicePage">
                <TableFilters
                    searchedEntity="invoice"
                    handleFilterChange={this.handleFilterChange}
                    resetFilters={this.resetFilters}
                    hasOneLocation={this.state.hasOneLocation}
                    clientList={this.props.clientList}
                    buList={this.props.buList}
                    yearFilter={true}
                    monthListFilter={true}
                    {...this.state.filterList}
                />
                { this.state.invoiceList.length > 0 ?
                    <div className="invoicePageContent">
                        <DynamicTable
                            headerList={HEADER_LIST}
                            data={this.formatInvoiceData(this.state.invoiceList)}
                            selectable={true}
                            selected={this.state.selectedInvoice ? [this.state.selectedInvoice] : null}
                            handleSelection={this.handleInvoiceSelection.bind(this)}
                            fixedHeader={true}
                            stripedRows={true}
                            page={this.state.page}
                            numberOfRowsPerPage={this.state.numberOfRowsPerPage}
                            totalRowNumber={this.state.totalRowNumber}
                            updatePagination={this.updatePagination.bind(this)}
                        >
                        </DynamicTable>
                        <RaisedButton className="exportCsv" label="CSV" secondary onClick={this.exportInvoiceListToCsv.bind(this)}/>
                    </div>
                    :
                    <div className="warningMessage">
                        {T.translate("invoicePage.invoiceListEmpty")}
                    </div>

                }
                <PdfViewerDialog
                    title={InvoiceService.getPdfViewerTitle(this.state.selectedInvoice)}
                    file={this.state.invoiceFile}
                    customErrorMessage={this.state.invoiceFileErrorMessage}
                    download={this.downloadSelectedInvoice.bind(this)}
                    show={this.state.showPdfViewer}
                    handleClose={this.handlePdfViewerClose.bind(this)}
                >
                </PdfViewerDialog>
            </div>
        );
    }

    /**********************************************************
     ****************** Appels Web Service ********************
     **********************************************************/

    getInvoiceList = () => {
        const getInvoiceListPromise = InvoiceService.getInvoiceList(
            this.state.page,
            this.state.numberOfRowsPerPage,
            this.state.filterList.location,
            this.state.filterList.client ? this.state.filterList.client.id : null,
            this.state.filterList.bu ? this.state.filterList.bu.id : null,
            this.state.filterList.year,
            this.state.filterList.monthList,
            this.state.filterList.search
        );

        const cancelablePromise = makeCancelable(getInvoiceListPromise);
        this._promises.forEach(promise => promise.cancel());
        this._promises.push(cancelablePromise);

        cancelablePromise
            .promise
            .then(res => {
                this.setState({
                    invoiceList: res.resourceList,
                    totalRowNumber: res.total,
                    isLoaded: true
                })
            })
            .catch((error) => {
                if (!error.isCanceled) {
                    this.setState({
                        error
                    });
                }
            })
            .then(() => this.promiseFulfilled(cancelablePromise));
    };

    getInvoiceFile() {
        if(this.state.selectedInvoice && this.state.selectedInvoice.id) {
            InvoiceService.checkInvoice(this.state.selectedInvoice.id)
                .then(res => {
                    const checkStatus = Object.keys(INVOICE_CHECK_STATUS).find(status => INVOICE_CHECK_STATUS[status].code === res.status);
                    if(INVOICE_CHECK_STATUS.SUCCESS.code === res.status) {
                        this.setState((state) => ({
                            showPdfViewer: true,
                            invoiceFile: {
                                url: InvoiceService.getInvoiceFileApiUrl(state.selectedInvoice.id),
                                withCredentials: true
                            }
                        }))
                    } else {
                        if(checkStatus) {
                            this.setState({
                                showPdfViewer: true,
                                invoiceFileErrorMessage: T.translate('invoicePage.pdfViewer.status.' + checkStatus)
                            })
                        }
                    }
                });
        }
    }

    downloadSelectedInvoice() {
        if(this.state.selectedInvoice && this.state.selectedInvoice.id) {
            InvoiceService.downloadInvoice(this.state.selectedInvoice.id);
        }
    }

    /**********************************************************
     ************************ Filtre  *************************
     **********************************************************/

    resetFilters() {
        this.setState({
            page: 1,
            filterList: {
                location: null,
                client: null,
                bu: null,
                searchText: null,
            }
        }, this.getInvoiceList);
    }

    handleFilterChange(filterKey, filterValue, debounce) {
        let filterList = this.state.filterList;
        filterList[filterKey] = filterValue;
        filterList = FilterService.syncFilters(filterKey, filterValue, filterList, this.props.clientList);
        this.setState({
            filterList,
            page: 1
        }, debounce ? this.getInvoiceListDebounced : this.getInvoiceList);
    }

    /**********************************************************
     ************************* Tableau ************************
     **********************************************************/

    formatInvoiceData(invoiceList) {
        return invoiceList.map(invoice => {
            let formattedInvoice = {...invoice};
            formattedInvoice.period = InvoiceService.getFormattedPeriod(formattedInvoice.period, "/");
            formattedInvoice.codeBu += " - " + formattedInvoice.buName;
            formattedInvoice.amount = InvoiceService.getAmountString(formattedInvoice.amount, true);
            return formattedInvoice;
        });
    }

    handleInvoiceSelection(selectedRows){
        const invoiceIndex = selectedRows[0];
        this.setState((state) => {
            return {
                selectedInvoice: state.invoiceList[invoiceIndex]
            }
        }, this.getInvoiceFile);
    }

    updatePagination(page, numberOfRowsPerPage){
        this.setState({
            page,
            numberOfRowsPerPage,
            selectedInvoice: null
        }, this.getInvoiceList);
    }

    /**********************************************************
     ************************* PDF Viewer *********************
     **********************************************************/

    handlePdfViewerClose() {
        this.setState({
            showPdfViewer: false,
            selectedInvoice: null,
            invoiceFile: null
        });
    }

    /**********************************************************
     ********************** Export CSV ************************
     **********************************************************/

    exportInvoiceListToCsv() {
        downloadCsv(InvoiceService.formatInvoiceListForCsv(this.state.invoiceList), InvoiceService.getCsvHeaderLabelList(), "invoice.csv")
    }

    /**********************************************************
     ***************** Gestion des promises *******************
     **********************************************************/

    /**
     * On retire la promise de la liste des promises en cours
     * Cette fonction doit etre appelé après que la promise est rempli
     * @param promise
     */
    promiseFulfilled(promise) {
        const index = this._promises.indexOf(promise);

        if (index !== -1) {
            this._promises.splice(index, 1);
        }
    };
}

export default connect(mapStateToProps)(InvoicePage);


// WEBPACK FOOTER //
// ./src/ui/invoice/InvoicePage.js